import React, { FC, useState, useEffect, useRef } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { formatChartData, getChartOptions, generateLegend } from '../../../../services/widget-chart-service';
import Select from '../../../../components/select';
import { useTheme } from 'styled-components';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

interface HypervisorChartProps {
  hypervisors: any[];
}

const HypervisorChart: FC<HypervisorChartProps> = ({ hypervisors }) => {
  const [selectedHypervisor, setSelectedHypervisor] = useState(hypervisors?.[0] ?? null);
  const [activeTab, setActiveTab] = useState<'memory' | 'disk' | 'vcpus'>('memory');
  const [chartData, setChartData] = useState<any>({ labels: [], datasets: [] });
  
  const legendRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const theme: any = useTheme();
  const palette = theme?.palette ?? {};
  const cardStyle: React.CSSProperties = {
    backgroundColor: palette[10] ?? '#ffffff',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: theme?.isDark ? '0 1px 3px rgba(15, 23, 42, 0.35)' : '0 1px 3px rgba(15, 23, 42, 0.08)',
    border: `1px solid ${palette[300] ?? '#e2e8f0'}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  };
  const headingColor = palette[850] ?? '#0f172a';
  const tabContainerBackground = palette[20] ?? '#f9fafb';
  const inactiveTabColor = palette[700] ?? '#64748b';
  const emptyStateColor = palette[700] ?? '#6b7280';
  const emptyIconOpacity = theme?.isDark ? 0.5 : 0.3;

  const currentHypervisor = selectedHypervisor;

  const getCurrentData = () => {
    if (!currentHypervisor) return { labels: [], data: [] };
    
    switch (activeTab) {
      case 'memory': {
        const labels = currentHypervisor.memory_labels?.map((label: string) =>
          typeof label === 'string' ? label.replace('(MB)', '(GB)') : label
        );
        const data = currentHypervisor.memory_data?.map((value: number) =>
          typeof value === 'number'
            ? Math.round((value / 1024) * 100) / 100
            : value
        );
        return { labels, data };
      }
      case 'disk':
        return {
          labels: currentHypervisor.disk_labels,
          data: currentHypervisor.disk_data
        };
      case 'vcpus':
        return {
          labels: currentHypervisor.vcpus_labels,
          data: currentHypervisor.vcpus_data
        };
      default:
        return { labels: [], data: [] };
    }
  };

  const updateChartData = () => {
    const { labels, data } = getCurrentData();
    const formattedData = formatChartData({ labels, data });
    setChartData(formattedData);
  };

  useEffect(() => {
    if (!selectedHypervisor && hypervisors?.length) {
      setSelectedHypervisor(hypervisors[0]);
    }
  }, [hypervisors, selectedHypervisor]);

  useEffect(() => {
    updateChartData();
  }, [selectedHypervisor, activeTab]);

  useEffect(() => {
    if (chartRef.current && legendRef.current) {
      legendRef.current.innerHTML = generateLegend(chartRef.current);
    }
  }, [chartData]);

  const chartOptions = getChartOptions(
    `Hypervisor ${activeTab}`, 
    `hypervisor-${activeTab}-doughnut-legend`
  );

  const getTabTitle = () => {
    switch (activeTab) {
      case 'memory':
        return 'Memory';
      case 'disk':
        return 'Disk';
      case 'vcpus':
        return 'vCPU';
      default:
        return 'Memory';
    }
  };

  // Prepare data for Select component
  const hypervisorOptions = hypervisors.map((hypervisor, index) => ({
    label: hypervisor.label,
    id: hypervisor,
    value: hypervisor
  }));

  if (!hypervisors || hypervisors.length === 0) {
    return <p>No hypervisor data available.</p>;
  }

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h2 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: headingColor,
          textAlign: 'center'
        }}>
          Hypervisor Resources
        </h2>
        
        <div>
          <Select
            title="Select Hypervisor"
            data={hypervisorOptions}
            selectedValue={selectedHypervisor}
            onChange={(value) => setSelectedHypervisor(value)}
            placeholder="Choose hypervisor"
            customStyles={{ marginBottom: '0' }}
          />
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          borderRadius: '8px',
          padding: '4px',
          gap: '4px',
          backgroundColor: tabContainerBackground
        }}>
          {(['memory', 'disk', 'vcpus'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '10px 18px',
                border: 'none',
                backgroundColor: activeTab === tab ? '#4671F6' : tabContainerBackground,
                color: activeTab === tab ? '#ffffff' : inactiveTabColor,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab ? '600' : '500',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                minWidth: '72px'
              }}
            >
              {tab === 'memory' ? 'Memory' : tab === 'disk' ? 'Disk' : 'vCPU'}
            </button>
          ))}
        </div>
      </div>

      {chartData.labels.length > 0 ? (
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <h3 style={{ 
            color: '#4671F6',
            fontSize: '14px',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            {getTabTitle()}
          </h3>
          <div style={{ 
            maxWidth: '280px', 
            position: 'relative',
            height: '220px'
          }}>
            <Doughnut
              ref={chartRef}
              data={chartData}
              options={chartOptions}
            />
          </div>
          <div ref={legendRef} style={{ 
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '8px',
            width: '100%'
          }} />
        </div>
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          color: emptyStateColor
        }}>
          <div>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
              opacity: emptyIconOpacity
            }}>
              📊
            </div>
            <p>No data available for chart.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HypervisorChart;
